import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Badge } from '../badge/badge';
import { Icon, type IconInput } from '../icon/icon';

/** Maps to Figma's `Size` property. Link Button ships md and sm only. */
export type LinkButtonSize = 'md' | 'sm';

/**
 * Text-only action — Figma's "Link Button".
 *
 * Works on either `<a>` or `<button>`, and which one you reach for is a real
 * decision rather than a style preference: use `<a href>` when it navigates, so
 * middle-click, right-click and Ctrl-click behave, and `<button>` when it acts on
 * the current page. Rendering one as the other is the most common accessibility
 * mistake in a component like this, so both are supported natively instead of
 * forcing a wrapper.
 *
 * ```html
 * <a ui-link-button href="/settings" tailIcon="arrow-right">Settings</a>
 * <button ui-link-button destructive leadIcon="trash-2">Remove</button>
 * ```
 *
 * Figma models 16 variants: Target(2) × Size(2) × State(4). Target and Size are
 * inputs; State is `:hover` / `:focus-visible` / `:disabled`, for the same reason
 * as Button.
 */
@Component({
  selector: 'a[ui-link-button], button[ui-link-button]',
  imports: [Icon, Badge],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './link-button.scss',
  host: {
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel() || null',
    // An <a> is not disable-able natively. aria-disabled plus pointer-events in
    // CSS is the accepted approximation; a disabled <button> needs neither.
    '[attr.aria-disabled]': 'disabledLink() ? "true" : null',
    '[attr.tabindex]': 'disabledLink() ? -1 : null',
  },
  template: `
    @if (leadIcon()) {
      <ui-icon [name]="leadIcon()!" />
    }

    <span class="ui-link-button__label"><ng-content /></span>

    @if (badge()) {
      <ui-badge color="gray">{{ badge() }}</ui-badge>
    }

    @if (tailIcon()) {
      <ui-icon [name]="tailIcon()!" />
    }
  `,
})
export class LinkButton {
  readonly size = input<LinkButtonSize>('md');

  /** Figma `Target=Destructive`. */
  readonly destructive = input(false, { transform: booleanAttribute });

  readonly leadIcon = input<IconInput>();
  readonly tailIcon = input<IconInput>();
  readonly badge = input<string>();
  readonly ariaLabel = input<string>();

  /**
   * Only for the `<a>` form, which cannot be natively disabled. On a `<button>`
   * use the native `disabled` attribute — this input would be a worse version of
   * it.
   */
  readonly disabledLink = input(false, { transform: booleanAttribute });

  protected readonly hostClasses = computed(() => {
    const classes = ['ui-link-button', `ui-link-button--${this.size()}`];
    if (this.destructive()) classes.push('ui-link-button--destructive');
    if (this.disabledLink()) classes.push('ui-link-button--disabled');
    return classes.join(' ');
  });
}
